import { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
export type SelectedType = 'registered' | 'instant';

export interface SelectionState {
  payeeSelected: boolean;
  bookDateSelected: boolean;
  dueDateSelected: boolean;
  invoiceRegistrationNumberSelected: boolean;
  instantPayeeNameSelected: boolean;
  bankNameSelected: boolean;
  bankBranchNameSelected: boolean;
  bankAccountTypeSelected: boolean;
  bankAccountNumberSelected: boolean;
  bankNameKanaSelected: boolean;
  titleSelected: boolean;
}

// isEnabledInstantPayee: Instant Payee setting is ON or report status is not unsubmitted.
// isActiveInstantPayee: Instant Payee data has already saved this report or not.
interface UseApplicationFormSelectionManagerProps {
  isOcrReadPayee?: boolean;
  isEnabledInstantPayee?: boolean;
  isActiveInstantPayee?: boolean;
  invoiceRegistrationNumberKey?: string;
}

type RecordMapping = Record<
  keyof SelectionState,
  string | Dayjs | null | undefined
>;
type PayeeFormDataInputIds = {
  bookDateInputId?: string;
  dueDateInputId?: string;
  payeeInputId?: string;
};
const initialSelectionState: SelectionState = {
  payeeSelected: true,
  bookDateSelected: true,
  dueDateSelected: true,
  invoiceRegistrationNumberSelected: true,
  instantPayeeNameSelected: true,
  bankNameSelected: true,
  bankBranchNameSelected: true,
  bankAccountTypeSelected: true,
  bankAccountNumberSelected: true,
  bankNameKanaSelected: true,
  titleSelected: true,
};

export const useApplicationFormSelectionManager = ({
  isOcrReadPayee,
  isEnabledInstantPayee,
  isActiveInstantPayee,
  invoiceRegistrationNumberKey,
}: UseApplicationFormSelectionManagerProps) => {
  const defaultSelectedType = useMemo((): SelectedType => {
    // Set default as 'instant' if OCR did not identify payee when instant payee setting is on
    if (!isOcrReadPayee && isEnabledInstantPayee) {
      return 'instant';
    } else {
      return 'registered';
    }
  }, [isOcrReadPayee, isEnabledInstantPayee]);

  const isVisibleInstantPayee = useMemo(() => {
    return !!isEnabledInstantPayee || !!isActiveInstantPayee;
  }, [isActiveInstantPayee, isEnabledInstantPayee]);

  const [isInstantPayeeOpened, setIsInstantPayeeOpened] =
    useState<SelectedType>(defaultSelectedType);

  useEffect(() => {
    setIsInstantPayeeOpened(defaultSelectedType);
  }, [defaultSelectedType]);

  const [selectionState, setSelectionState] = useState<SelectionState>(
    initialSelectionState
  );

  // Initialize all selection states
  const initializeSelection = useCallback(() => {
    setSelectionState(initialSelectionState);
  }, []);

  // Update individual state
  const updateSelection = useCallback(
    (key: keyof SelectionState, value: boolean) => {
      setSelectionState((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  // Update multiple states at once
  const updateMultipleSelections = useCallback(
    (updates: Partial<SelectionState>) => {
      setSelectionState((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  const checkedList = useMemo(() => {
    const values = [
      selectionState.bookDateSelected,
      selectionState.dueDateSelected,
      selectionState.invoiceRegistrationNumberSelected,
      selectionState.titleSelected,
    ];
    if (isInstantPayeeOpened === 'instant') {
      values.push(
        selectionState.instantPayeeNameSelected,
        selectionState.bankNameSelected,
        selectionState.bankBranchNameSelected,
        selectionState.bankAccountTypeSelected,
        selectionState.bankAccountNumberSelected,
        selectionState.bankNameKanaSelected
      );
    } else {
      values.push(selectionState.payeeSelected);
    }
    return values;
  }, [isInstantPayeeOpened, selectionState]);

  const allChecked = useMemo(() => checkedList.every(Boolean), [checkedList]);
  const someChecked = useMemo(() => checkedList.some(Boolean), [checkedList]);
  const indeterminate = useMemo(
    () => someChecked && !allChecked,
    [someChecked, allChecked]
  );

  // Define field paths
  const fieldPaths = useMemo(
    () => ({
      payeeSelected: 'payee.value',
      payeeReportForm: 'payee.reportForm',
      bookDateSelected: 'payee.bookDate',
      dueDateSelected: 'payee.dueDate',
      invoiceRegistrationNumberSelected: invoiceRegistrationNumberKey
        ? `reportForm.${invoiceRegistrationNumberKey}`
        : undefined,
      instantPayeeNameSelected: 'payee.instantPayee.name',
      bankNameSelected: 'payee.instantPayee.bankId',
      bankBranchNameSelected: 'payee.instantPayee.bankBranchId',
      bankAccountTypeSelected: 'payee.instantPayee.accountType',
      bankAccountNumberSelected: 'payee.instantPayee.number',
      bankNameKanaSelected: 'payee.instantPayee.holderNameKana',
      titleSelected: 'title',
    }),
    [invoiceRegistrationNumberKey]
  );

  // Helper function to create nested object structure from field path
  const setNestedValue = useCallback(
    (
      result: Record<string, unknown>,
      fieldPath: string,
      value: string | Dayjs | null | undefined
    ) => {
      const pathParts = fieldPath.split('.');
      pathParts.reduce((currentLevel, part, index) => {
        if (index === pathParts.length - 1) {
          // Last part - assign the value
          currentLevel[part] = value;
          return currentLevel;
        } else {
          // Not the last part - create nested object if it doesn't exist
          if (!(part in currentLevel)) {
            currentLevel[part] = {};
          }
          const nextLevel = currentLevel[part];
          if (typeof nextLevel === 'object' && nextLevel !== null) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return nextLevel as Record<string, unknown>;
          }
          return currentLevel;
        }
      }, result);
    },
    []
  );

  const getMappedValues = useCallback(
    (mappingOcrInferenceResultToSelectionState: RecordMapping) => {
      const result: Record<string, unknown> = {};

      // Process registered payee specific fields
      const registeredFields: Array<keyof SelectionState> = [
        'bookDateSelected',
        'dueDateSelected',
        'invoiceRegistrationNumberSelected',
        'titleSelected',
      ];

      registeredFields.forEach((key) => {
        if (
          selectionState[key] &&
          key in fieldPaths &&
          key in mappingOcrInferenceResultToSelectionState
        ) {
          const fieldPath = fieldPaths[key];
          const value = mappingOcrInferenceResultToSelectionState[key];
          if (fieldPath) {
            setNestedValue(result, fieldPath, value);
          }
        }
      });
      return result;
    },
    [fieldPaths, selectionState, setNestedValue]
  );

  const getInstantPayeeMappedValues = useCallback(
    (
      mappingOcrInferenceResultToSelectionState: RecordMapping
    ): Record<string, unknown> => {
      const result: Record<string, unknown> = getMappedValues(
        mappingOcrInferenceResultToSelectionState
      );

      setNestedValue(result, fieldPaths.payeeSelected, '1006');

      const instantFields: Array<keyof SelectionState> = [
        'instantPayeeNameSelected',
        'bankNameSelected',
        'bankBranchNameSelected',
        'bankAccountTypeSelected',
        'bankAccountNumberSelected',
        'bankNameKanaSelected',
      ];

      [...instantFields].forEach((key) => {
        if (
          selectionState[key] &&
          key in fieldPaths &&
          key in mappingOcrInferenceResultToSelectionState
        ) {
          const fieldPath = fieldPaths[key];
          const value = mappingOcrInferenceResultToSelectionState[key];
          if (fieldPath) {
            setNestedValue(result, fieldPath, value);
          }
        }
      });

      return result;
    },
    [fieldPaths, getMappedValues, selectionState, setNestedValue]
  );

  // Helper function to handle registered payee case
  const getRegisteredPayeeMappedValues = useCallback(
    (
      mappingOcrInferenceResultToSelectionState: RecordMapping,
      payeeFormDataInputIds: PayeeFormDataInputIds
    ): Record<string, unknown> => {
      const result: Record<string, unknown> = getMappedValues(
        mappingOcrInferenceResultToSelectionState
      );

      // Process registered payee specific fields
      const registeredFields: Array<keyof SelectionState> = ['payeeSelected'];

      registeredFields.forEach((key) => {
        if (
          selectionState[key] &&
          key in fieldPaths &&
          key in mappingOcrInferenceResultToSelectionState
        ) {
          const fieldPath = fieldPaths[key];
          const value = mappingOcrInferenceResultToSelectionState[key];
          if (fieldPath) {
            setNestedValue(result, fieldPath, value);
          }
        }
      });

      setNestedValue(
        result,
        'payee.reportForm.bookDate.reportFormInputId',
        payeeFormDataInputIds.bookDateInputId
      );
      setNestedValue(
        result,
        'payee.reportForm.dueDate.reportFormInputId',
        payeeFormDataInputIds.dueDateInputId
      );
      setNestedValue(
        result,
        'payee.reportForm.payee.reportFormInputId',
        payeeFormDataInputIds.payeeInputId
      );
      return result;
    },
    [fieldPaths, getMappedValues, selectionState, setNestedValue]
  );

  // Map fieldPaths values to mappingOcrInferenceResultToSelectionState values where keys match
  const getMappedFieldValues = useCallback(
    (
      mappingOcrInferenceResultToSelectionState: RecordMapping,
      payeeFormDataInputIds: PayeeFormDataInputIds
    ): Record<string, unknown> => {
      if (isInstantPayeeOpened === 'instant') {
        return getInstantPayeeMappedValues(
          mappingOcrInferenceResultToSelectionState
        );
      } else {
        return getRegisteredPayeeMappedValues(
          mappingOcrInferenceResultToSelectionState,
          payeeFormDataInputIds
        );
      }
    },
    [
      isInstantPayeeOpened,
      getInstantPayeeMappedValues,
      getRegisteredPayeeMappedValues,
    ]
  );

  const toggleAllChecked = useCallback(() => {
    const allChecked = checkedList.every(Boolean);
    const newValue = !allChecked;

    if (isInstantPayeeOpened === 'instant') {
      const instantUpdates = {
        instantPayeeNameSelected: newValue,
        bankNameSelected: newValue,
        bankBranchNameSelected: newValue,
        bankAccountTypeSelected: newValue,
        bankAccountNumberSelected: newValue,
        bankNameKanaSelected: newValue,
        bookDateSelected: newValue,
        dueDateSelected: newValue,
        invoiceRegistrationNumberSelected: newValue,
      };
      updateMultipleSelections(instantUpdates);
    } else {
      const registeredUpdates: Partial<SelectionState> = {
        payeeSelected: newValue,
        bookDateSelected: newValue,
        dueDateSelected: newValue,
        invoiceRegistrationNumberSelected: newValue,
        titleSelected: newValue,
      };
      updateMultipleSelections(registeredUpdates);
    }
  }, [checkedList, isInstantPayeeOpened, updateMultipleSelections]);

  return {
    // State
    isInstantPayeeOpened,
    defaultSelectedType,
    setIsInstantPayeeOpened,
    selectionState,
    isVisibleInstantPayee,
    isEnabledInstantPayee,

    // Actions
    updateSelection,
    initializeSelection,
    updateMultipleSelections,
    toggleAllChecked,
    getMappedFieldValues,

    // Computed values
    checkedList,
    allChecked,
    someChecked,
    indeterminate,
    fieldPaths,

    ...selectionState,
  };
};
